import { useState, useContext, useRef, useEffect } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Cherry, RotateCw, History, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { celebrateWin, soundEffects } from "@/lib/celebrations";

const SYMBOLS = ["🍋", "🍒", "🍊", "⭐", "💎", "7️⃣"];

export const SlotsPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [reels, setReels] = useState(["❓", "❓", "❓"]);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const reelRefs = useRef([null, null, null]);

  useEffect(() => {
    soundEffects.setEnabled(soundEnabled);
  }, [soundEnabled]);

  const spinReels = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }

    setLoading(true);
    setSpinning(true);
    soundEffects.playSpin();
    
    // Animation
    const animationDuration = 2000;
    const intervals = reelRefs.current.map((_, idx) => {
      let count = 0;
      return setInterval(() => {
        setReels(prev => {
          const newReels = [...prev];
          newReels[idx] = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
          return newReels;
        });
        count++;
      }, 100);
    });

    try {
      const response = await axios.post(`${API}/games/slots/play`, {
        game_type: "slots",
        bet_amount: betAmount
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Stop reels one by one with delay
      setTimeout(() => {
        clearInterval(intervals[0]);
        setReels(prev => [response.data.reels[0], prev[1], prev[2]]);
        soundEffects.playClick();
      }, animationDuration * 0.5);
      
      setTimeout(() => {
        clearInterval(intervals[1]);
        setReels(prev => [prev[0], response.data.reels[1], prev[2]]);
        soundEffects.playClick();
      }, animationDuration * 0.7);
      
      setTimeout(() => {
        clearInterval(intervals[2]);
        setReels(response.data.reels);
        setSpinning(false);
        setLoading(false);
        setLastResult(response.data);
        setHistory(prev => [response.data, ...prev.slice(0, 9)]);
        
        if (response.data.won) {
          toast.success(`Won ₹${response.data.winnings.toFixed(2)}! ${response.data.multiplier}x multiplier!`);
          celebrateWin(response.data.multiplier, response.data.winnings);
        } else {
          toast.error("No match - try again!");
          soundEffects.playLoss();
        }
        
        refreshBalance();
      }, animationDuration);
      
    } catch (error) {
      intervals.forEach(i => clearInterval(i));
      setSpinning(false);
      setLoading(false);
      toast.error(error.response?.data?.detail || "Game error");
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#FF007A]/20 flex items-center justify-center">
              <Cherry className="w-6 h-6 text-[#FF007A]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="slots-title">Slots</h1>
              <p className="text-[#A1A1AA] text-sm">Match symbols to win big!</p>
            </div>
          </div>
          <button 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white"
            data-testid="sound-toggle"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Area */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#1A1B1F] border border-white/[0.08] overflow-hidden">
              {/* Slot Machine */}
              <div className="p-8 bg-gradient-to-b from-[#2D0A3E] to-[#1A1B1F]">
                {/* Reels */}
                <div className="flex justify-center gap-4 mb-8">
                  {reels.map((symbol, idx) => (
                    <div
                      key={idx}
                      ref={el => reelRefs.current[idx] = el}
                      className={`
                        w-24 h-28 rounded-xl bg-[#0F0F10] border-4 border-[#FFD600]
                        flex items-center justify-center text-5xl
                        shadow-[inset_0_2px_10px_rgba(0,0,0,0.5),0_0_20px_rgba(255,214,0,0.3)]
                        ${spinning ? 'animate-pulse' : ''}
                      `}
                      data-testid={`slot-reel-${idx}`}
                    >
                      {symbol}
                    </div>
                  ))}
                </div>
                
                {/* Result Display */}
                {lastResult && !spinning && (
                  <div className={`text-center p-4 rounded-lg ${
                    lastResult.won ? 'bg-[#00E701]/20' : 'bg-[#FF3B30]/20'
                  }`}>
                    <span className={`text-2xl font-bold ${
                      lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'
                    }`}>
                      {lastResult.won 
                        ? `WIN! ${lastResult.multiplier}x - ₹${lastResult.winnings.toFixed(2)}`
                        : 'No Match'
                      }
                    </span>
                  </div>
                )}
              </div>
              
              {/* Controls */}
              <div className="p-6 border-t border-white/[0.08]">
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount (₹)</label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      disabled={spinning}
                      className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                      data-testid="slots-bet-input"
                    />
                  </div>
                  
                  <div className="flex gap-2 items-end">
                    {[50, 100, 500, 1000].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setBetAmount(amount)}
                        disabled={spinning}
                        className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                      >
                        {amount}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={spinReels}
                  disabled={loading || betAmount <= 0 || betAmount > (user?.balance || 0)}
                  className="w-full h-14 bg-[#FF007A] hover:bg-[#E60070] text-white font-bold text-lg shadow-[0_0_20px_rgba(255,0,122,0.4)]"
                  data-testid="slots-spin-btn"
                >
                  <RotateCw className={`w-5 h-5 mr-2 ${spinning ? 'animate-spin' : ''}`} />
                  {spinning ? 'SPINNING...' : 'SPIN'}
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Paytable */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4">Paytable</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 rounded bg-white/5">
                  <span>💎 💎 💎</span>
                  <span className="text-[#00E701] font-bold">50x</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-white/5">
                  <span>7️⃣ 7️⃣ 7️⃣</span>
                  <span className="text-[#00E701] font-bold">25x</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-white/5">
                  <span>⭐ ⭐ ⭐</span>
                  <span className="text-[#00E701] font-bold">10x</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-white/5">
                  <span>🍒/🍊/🍋 x3</span>
                  <span className="text-[#00E701] font-bold">5x</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-white/5">
                  <span>Any 2 match</span>
                  <span className="text-[#FFD600] font-bold">2x</span>
                </div>
              </div>
            </div>

            {/* History */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">Recent Spins</h3>
              </div>
              
              <div className="space-y-2">
                {history.length > 0 ? history.map((h, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded ${
                      h.won ? 'bg-[#00E701]/10' : 'bg-white/5'
                    }`}
                  >
                    <span className="text-lg">{h.reels?.join(' ')}</span>
                    <span className={`font-mono text-sm ${h.won ? 'text-[#00E701]' : 'text-[#52525B]'}`}>
                      {h.won ? `+₹${h.winnings?.toFixed(0)}` : '-'}
                    </span>
                  </div>
                )) : (
                  <p className="text-[#52525B] text-sm text-center py-4">No spins yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
